/**
 * Pipedrive Activity Detail Page
 * Shows activity details with related records
 */

import { useParams, Link } from 'react-router-dom'
import { usePipedriveActivity } from '@/hooks/use-pipedrive-api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Calendar,
  ArrowLeft,
  Clock,
  CheckCircle,
  Building2,
  Briefcase,
  User,
  MapPin,
  StickyNote,
  ExternalLink,
  Tag,
  AlertTriangle,
  UserCheck,
} from 'lucide-react'
import { SEMANTIC, CATEGORICAL } from '@/lib/theme'

export default function PipedriveActivityDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: activity, isLoading, error } = usePipedriveActivity(id ?? '')

  const getStatusBadge = (done: string | null) => {
    if (done === 'Yes') {
      return (
        <Badge
          variant="secondary"
          style={{
            backgroundColor: SEMANTIC.success.fill,
            color: SEMANTIC.success.text,
          }}
        >
          <CheckCircle className="h-3 w-3 mr-1" aria-hidden="true" />
          Done
        </Badge>
      )
    }
    return (
      <Badge
        variant="secondary"
        style={{
          backgroundColor: SEMANTIC.warning.fill,
          color: SEMANTIC.warning.text,
        }}
      >
        <Clock className="h-3 w-3 mr-1" aria-hidden="true" />
        Pending
      </Badge>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg" role="alert">
          Failed to load activity. Please try again later.
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    )
  }

  if (!activity) {
    return (
      <div className="p-6">
        <div className="bg-muted p-4 rounded-lg">Activity not found.</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/pipedrive/activities">
          <Button variant="ghost" size="icon" aria-label="Back to activities">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Calendar className="h-6 w-6" aria-hidden="true" />
              {activity.subject || 'Untitled Activity'}
            </h1>
            {getStatusBadge(activity.done)}
            {activity.type && <Badge variant="outline">{activity.type}</Badge>}
          </div>
          <p className="text-muted-foreground">
            {activity.assigned_to ? `Assigned to: ${activity.assigned_to}` : ''}
            {activity.assigned_to && activity.due_date ? ' • ' : ''}
            {activity.due_date ? `Due: ${activity.due_date}` : ''}
          </p>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Activity Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span className="text-muted-foreground">Type:</span>
              <span className="font-semibold">{activity.type || '-'}</span>
            </div>
            <div className="flex items-center gap-2">
              {activity.done === 'Yes' ? (
                <CheckCircle
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.evergreen.solid }}
                  aria-hidden="true"
                />
              ) : (
                <Clock
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.clay.solid }}
                  aria-hidden="true"
                />
              )}
              <span className="text-muted-foreground">Status:</span>
              <span className="font-semibold">{activity.done === 'Yes' ? 'Done' : 'Pending'}</span>
              {activity.marked_as_done_time && (
                <span className="text-sm text-muted-foreground">
                  ({activity.marked_as_done_time})
                </span>
              )}
            </div>
            {activity.priority && (
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Priority:</span>
                <span className="font-semibold">{activity.priority}</span>
              </div>
            )}
            {activity.due_date && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Due Date:</span>
                <span className="font-semibold">
                  {activity.due_date}
                  {activity.due_time ? ` at ${activity.due_time}` : ''}
                </span>
              </div>
            )}
            {activity.duration && (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Duration:</span>
                <span className="font-semibold">{activity.duration}</span>
              </div>
            )}
            {activity.location && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Location:</span>
                <span className="font-semibold">{activity.location}</span>
              </div>
            )}
            {activity.assigned_to && (
              <div className="flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Assigned To:</span>
                <span className="font-semibold">{activity.assigned_to}</span>
              </div>
            )}
            {activity.creator && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Creator:</span>
                <span className="font-semibold">{activity.creator}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Related Records</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activity.organization_id ? (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Organization:</span>
                <Link
                  to={`/pipedrive/organizations/${activity.organization_id}`}
                  className="hover:underline flex items-center gap-1 hover:opacity-70"
                  style={{ color: CATEGORICAL.navy.solid }}
                >
                  {activity.organization || 'View Organization'}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </Link>
              </div>
            ) : activity.organization ? (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Organization:</span>
                <span>{activity.organization}</span>
              </div>
            ) : null}

            {activity.deal_id ? (
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Deal:</span>
                <Link
                  to={`/pipedrive/deals/${activity.deal_id}`}
                  className="hover:underline flex items-center gap-1 hover:opacity-70"
                  style={{ color: CATEGORICAL.navy.solid }}
                >
                  {activity.deal || 'View Deal'}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </Link>
              </div>
            ) : activity.deal ? (
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Deal:</span>
                <span>{activity.deal}</span>
              </div>
            ) : null}

            {activity.person_id || activity.contact_person_id ? (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Contact:</span>
                <Link
                  to={`/pipedrive/people/${activity.person_id || activity.contact_person_id}`}
                  className="hover:underline flex items-center gap-1 hover:opacity-70"
                  style={{ color: CATEGORICAL.navy.solid }}
                >
                  {activity.person || activity.contact_person || 'View Contact'}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </Link>
              </div>
            ) : activity.person || activity.contact_person ? (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Contact:</span>
                <span>{activity.person || activity.contact_person}</span>
              </div>
            ) : null}

            {!activity.organization &&
              !activity.deal &&
              !activity.person &&
              !activity.contact_person && (
                <p className="text-muted-foreground text-sm">No related records</p>
              )}
          </CardContent>
        </Card>
      </div>

      {/* Note */}
      {activity.note && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5" aria-hidden="true" />
              Note
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="prose prose-sm max-w-none text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: activity.note }}
            />
          </CardContent>
        </Card>
      )}

      {/* Metadata */}
      {(activity.add_time || activity.update_time) && (
        <div className="text-sm text-muted-foreground">
          {activity.add_time && <span>Created: {activity.add_time}</span>}
          {activity.add_time && activity.update_time && <span className="mx-2">•</span>}
          {activity.update_time && <span>Updated: {activity.update_time}</span>}
        </div>
      )}
    </div>
  )
}
